/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import {
	MEMBER_LIST_RANGE_MAX_END,
	MEMBER_LIST_RANGE_MAX_SPAN,
	MEMBER_LIST_RANGE_PAGE_SIZE,
	MEMBER_LIST_RANGE_WINDOW_OVERSCAN_PAGES,
} from '@fluxer/constants/src/GatewayConstants';

export interface MemberListRangeWindowOptions {
	scrollTop: number;
	clientHeight: number;
	rowHeight: number;
	bufferRows: number;
	totalRows?: number | null;
}

export function normalizeMemberListRanges(inputRanges: Array<[number, number]>): Array<[number, number]> {
	if (inputRanges.length === 0) {
		return [];
	}

	const sanitizedRanges = inputRanges
		.map(([start, end]) => {
			const safeStart = Math.max(0, Math.floor(start));
			const safeEnd = Math.min(MEMBER_LIST_RANGE_MAX_END, Math.max(safeStart, Math.floor(end)));
			if (safeStart > MEMBER_LIST_RANGE_MAX_END) {
				return null;
			}
			return [safeStart, safeEnd] as [number, number];
		})
		.filter((range): range is [number, number] => range != null)
		.sort(([leftStart], [rightStart]) => leftStart - rightStart);

	if (sanitizedRanges.length === 0) {
		return [];
	}

	const mergedRanges: Array<[number, number]> = [sanitizedRanges[0]];
	for (const [start, end] of sanitizedRanges.slice(1)) {
		const previousRange = mergedRanges[mergedRanges.length - 1];
		if (start <= previousRange[1] + 1) {
			previousRange[1] = Math.max(previousRange[1], end);
			continue;
		}
		mergedRanges.push([start, end]);
	}

	return mergedRanges.flatMap((range) => splitMemberListRangeBySpan(range));
}

export function buildMemberListRangeWindow(options: MemberListRangeWindowOptions): Array<[number, number]> {
	const {scrollTop, clientHeight, rowHeight, bufferRows, totalRows} = options;

	if (rowHeight <= 0) {
		return [[0, MEMBER_LIST_RANGE_MAX_SPAN]];
	}

	const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - bufferRows);
	const unclampedEnd = Math.max(startIndex, Math.ceil((scrollTop + clientHeight) / rowHeight) + bufferRows);
	const safeTotalRows = totalRows != null && Number.isFinite(totalRows) ? Math.max(0, Math.floor(totalRows)) : null;
	const endIndex =
		safeTotalRows != null && safeTotalRows > 0 ? Math.min(unclampedEnd, safeTotalRows - 1) : unclampedEnd;

	const firstVisiblePage = Math.floor(startIndex / MEMBER_LIST_RANGE_PAGE_SIZE);
	const lastVisiblePage = Math.floor(endIndex / MEMBER_LIST_RANGE_PAGE_SIZE);

	let firstPage = Math.max(0, firstVisiblePage - MEMBER_LIST_RANGE_WINDOW_OVERSCAN_PAGES);
	let lastPage = Math.max(firstPage, lastVisiblePage + MEMBER_LIST_RANGE_WINDOW_OVERSCAN_PAGES);

	if (safeTotalRows != null && safeTotalRows > 0) {
		const maxPage = Math.floor((safeTotalRows - 1) / MEMBER_LIST_RANGE_PAGE_SIZE);
		firstPage = Math.min(firstPage, maxPage);
		lastPage = Math.min(lastPage, maxPage);
	}

	const ranges: Array<[number, number]> = [];
	for (let page = firstPage; page <= lastPage; page += 1) {
		const pageStart = page * MEMBER_LIST_RANGE_PAGE_SIZE;
		const maxPageEnd = pageStart + MEMBER_LIST_RANGE_MAX_SPAN;
		const pageEnd = safeTotalRows != null && safeTotalRows > 0 ? Math.min(maxPageEnd, safeTotalRows - 1) : maxPageEnd;
		ranges.push([pageStart, pageEnd]);
	}

	return normalizeMemberListRanges(ranges);
}

export function isIndexInMemberListRanges(index: number, ranges: Array<[number, number]>): boolean {
	for (const [start, end] of ranges) {
		if (index >= start && index <= end) {
			return true;
		}
	}
	return false;
}

function splitMemberListRangeBySpan(range: [number, number]): Array<[number, number]> {
	const [start, end] = range;
	const chunks: Array<[number, number]> = [];
	let chunkStart = start;

	while (chunkStart <= end) {
		const chunkEnd = Math.min(end, chunkStart + MEMBER_LIST_RANGE_MAX_SPAN);
		chunks.push([chunkStart, chunkEnd]);
		chunkStart = chunkEnd + 1;
	}

	return chunks;
}
